/**
 * The one list of fields `PATCH /api/cmmd/projects/:id` carries.
 *
 * Three places used to keep their own copy of this list: the client's
 * `UpdateProjectParams` interface, the client's request-body builder, and the
 * server's proxy allowlist. Nothing bound them together, and the failure is
 * silent in the worst direction — a field the UI sends but the server allowlist
 * has never heard of is dropped on the way through, so the save returns 200 and
 * the value is simply gone on the next load. That is exactly how the project
 * `icon` shipped unsaveable: the control, the contract, and the optimistic cache
 * update all landed, and the two middle layers were never told the field exists.
 *
 * So the list lives here once, as data. The interface is derived from it, the
 * body builder iterates it, and the server allowlist iterates it. Adding a field
 * is one edit in one file, and there is no longer a copy that can be missed.
 */

/**
 * How a field's value is validated at the trust boundary.
 *
 * The server cannot trust its request body, so each field still needs a runtime
 * check — but the check is a property of the field, not of the layer, so it is
 * declared alongside the field rather than re-derived per call site.
 */
export type CmmdProjectPatchFieldKind =
  /** A non-empty string. Blank is rejected rather than stored. */
  | "requiredText"
  /** A string, or null to clear. */
  | "nullableText"
  /** An id: number or string (CMMD accepts both), or null to unset. */
  | "nullableId";

export const CMMD_PROJECT_PATCH_FIELDS = {
  /** The board name of record. Renaming here renames the project everywhere. */
  title: "requiredText",
  /**
   * The project's display name inside Forge. Null falls back to `title`.
   *
   * Deliberately separate from `title`: a portfolio board and a working sidebar
   * want different names for the same project, and holding the second one as
   * client-local state is what would let the two drift apart.
   */
  shortName: "nullableText",
  workItemPrefix: "nullableText",
  status: "nullableText",
  priority: "nullableText",
  campaignId: "nullableId",
  description: "nullableText",
  brief: "nullableText",
  ownerId: "nullableId",
  startDate: "nullableText",
  targetEndDate: "nullableText",
  teamId: "nullableId",
  githubRepoUrl: "nullableText",
  /**
   * The project's emoji, and only the emoji. `iconUrl` is CMMD-owned — it is an
   * S3 key written by the repository icon sync, and a client that could set it
   * would control what every reader's <img> loads.
   */
  icon: "nullableText",
} as const satisfies Record<string, CmmdProjectPatchFieldKind>;

export type CmmdProjectPatchField = keyof typeof CMMD_PROJECT_PATCH_FIELDS;

export const CMMD_PROJECT_PATCH_FIELD_NAMES = Object.keys(
  CMMD_PROJECT_PATCH_FIELDS,
) as readonly CmmdProjectPatchField[];

/** Maps a field kind to the value type the layer above the wire uses. */
type ValueForKind<K extends CmmdProjectPatchFieldKind> = K extends "requiredText"
  ? string
  : K extends "nullableText"
    ? string | null
    : string | null;

/**
 * The patch body, derived from the field list so the type cannot fall behind it.
 *
 * Ids are typed `string | null` here even though CMMD also accepts numbers: the
 * client already carries CMMD ids as strings, and narrowing the type keeps call
 * sites from inventing a second representation.
 */
export type CmmdProjectPatchBody = {
  readonly [Field in CmmdProjectPatchField]?: ValueForKind<
    (typeof CMMD_PROJECT_PATCH_FIELDS)[Field]
  >;
};

/**
 * Accept a single field's value at the trust boundary, or reject it.
 *
 * Returns `{ value }` when the field should be forwarded and `null` when it
 * should be ignored — distinct from a forwarded `null`, which means "clear it".
 */
export function acceptCmmdProjectPatchField(
  field: CmmdProjectPatchField,
  raw: unknown,
): { readonly value: unknown } | null {
  switch (CMMD_PROJECT_PATCH_FIELDS[field]) {
    case "requiredText": {
      if (typeof raw !== "string") return null;
      const trimmed = raw.trim();
      return trimmed.length === 0 ? null : { value: trimmed };
    }
    case "nullableText":
      return typeof raw === "string" || raw === null ? { value: raw } : null;
    case "nullableId":
      return typeof raw === "number" || typeof raw === "string" || raw === null
        ? { value: raw }
        : null;
  }
}
